import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Slider,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  getProductSearchCellValue,
  getProductSearchColumnLabel,
  getProductSearchRawCellValue,
  PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH,
  truncateProductSearchPreviewText,
} from "./productSearchRenderHelpers";

const HOUSE_SELLER_CONSIGNOR_ID = "4b78d21b-a";
const HOUSE_SELLER_CONSIGNOR_NAME = "house seller";

const normalizeConsignorValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

export default function ProductSearchTab({
  primary,
  productSearchBusy,
  productSearchLoadedCount,
  productSearchTotalCount,
  productSearchAllRowsCount,
  productSearchExportBusy,
  onOpenRefreshConfirm,
  onExportFilteredCsv,
  productSearchField,
  setProductSearchField,
  productSearchSearchInput,
  setProductSearchSearchInput,
  setProductSearchVisibleCount,
  productSearchErr,
  productSearchLoadedOnce,
  productSearchRows,
  productSearchSortedLength,
  productSearchDisplayColumns,
  productSearchSortKey,
  productSearchSortDir,
  onProductSearchSort,
  productSearchContainerRef,
  onProductSearchScroll,
  productSearchVisibleCount,
  formatNumber2,
  toAbsInt,
  safeNum,
  moneyPlain,
  getProductSearchThumbnailUrl,
  getLotDescriptionText,
  formatLotDimensions,
  getListernautMediaUrls,
}) {
  const [fullTextDialog, setFullTextDialog] = useState({
    open: false,
    title: "Full Description",
    text: "",
  });
  const [upcHistoryDialog, setUpcHistoryDialog] = useState({
    open: false,
    upc: "",
    soldRows: [],
    passedRows: [],
  });
  const [upcHistoryTab, setUpcHistoryTab] = useState("sold");
  const [upcHistoryConsignorFilter, setUpcHistoryConsignorFilter] = useState("all");
  const [upcHistoryOutcomeFilter, setUpcHistoryOutcomeFilter] = useState("all");
  const [upcHistoryRunningAuctionsFilter, setUpcHistoryRunningAuctionsFilter] = useState("exclude");
  const [upcHistoryRetailSpendPct, setUpcHistoryRetailSpendPct] = useState(10);
  const [expandedMediaUrlsByKey, setExpandedMediaUrlsByKey] = useState({});

  const openFullTextDialog = useCallback((title, text) => {
    const nextText = String(text || "");
    if (!nextText.trim()) return;
    setFullTextDialog({
      open: true,
      title: String(title || "Full Description").trim() || "Full Description",
      text: nextText,
    });
  }, []);

  const closeFullTextDialog = useCallback(() => {
    setFullTextDialog({ open: false, title: "Full Description", text: "" });
  }, []);

  const openUpcHistoryDialog = useCallback((row) => {
    const upc = String(row?.upc || row?.listernautUpc || "").trim();
    if (!upc) return;
    setUpcHistoryDialog({
      open: true,
      upc,
      soldRows: Array.isArray(row?.upcOccurrenceDetails?.soldRows) ? row.upcOccurrenceDetails.soldRows : [],
      passedRows: Array.isArray(row?.upcOccurrenceDetails?.passedRows) ? row.upcOccurrenceDetails.passedRows : [],
    });
    setUpcHistoryTab("sold");
    setUpcHistoryConsignorFilter("all");
    setUpcHistoryOutcomeFilter("all");
    setUpcHistoryRunningAuctionsFilter("exclude");
    setUpcHistoryRetailSpendPct(10);
  }, []);

  const closeUpcHistoryDialog = useCallback(() => {
    setUpcHistoryDialog({ open: false, upc: "", soldRows: [], passedRows: [] });
    setUpcHistoryTab("sold");
    setUpcHistoryConsignorFilter("all");
    setUpcHistoryOutcomeFilter("all");
    setUpcHistoryRunningAuctionsFilter("exclude");
    setUpcHistoryRetailSpendPct(10);
  }, []);
  const getSortedUpcHistoryRows = useCallback(
    (rows = []) =>
      [...(Array.isArray(rows) ? rows : [])].sort(
        (a, b) => safeNum(b?.bid ?? b?.highBid ?? 0) - safeNum(a?.bid ?? a?.highBid ?? 0)
      ),
    [safeNum]
  );

  const isHouseSellerRow = useCallback((row) => {
    const consignorName = normalizeConsignorValue(
      row?.consignor || row?.listernautConsignorName || row?.sellerCode || row?.listernautSellerCode || ""
    );
    const consignorId = normalizeConsignorValue(
      row?.consignorId || row?.listernautConsignorId || row?.sellerCode || row?.listernautSellerCode || ""
    );
    return consignorName === HOUSE_SELLER_CONSIGNOR_NAME || consignorId === normalizeConsignorValue(HOUSE_SELLER_CONSIGNOR_ID);
  }, []);

  const upcHistoryFilteredRows = useMemo(() => {
    const soldRows = Array.isArray(upcHistoryDialog.soldRows) ? upcHistoryDialog.soldRows : [];
    const passedRows = Array.isArray(upcHistoryDialog.passedRows) ? upcHistoryDialog.passedRows : [];
    const sourceRows = upcHistoryOutcomeFilter === "all" ? [...soldRows, ...passedRows] : soldRows;
    const consignorFilteredRows =
      upcHistoryConsignorFilter === "house" ? sourceRows.filter((row) => isHouseSellerRow(row)) : sourceRows;
    return getSortedUpcHistoryRows(consignorFilteredRows);
  }, [
    getSortedUpcHistoryRows,
    isHouseSellerRow,
    upcHistoryConsignorFilter,
    upcHistoryDialog.passedRows,
    upcHistoryDialog.soldRows,
    upcHistoryOutcomeFilter,
  ]);

  const upcHistoryBuyCalculator = useMemo(() => {
    const nowMs = Date.now();
    const rows = (Array.isArray(upcHistoryFilteredRows) ? upcHistoryFilteredRows : []).filter((row) => {
      if (upcHistoryRunningAuctionsFilter === "include") return true;
      const endMs = Number(row?.auctionEndAtMs ?? row?.auctionEndDate ?? 0) || 0;
      return endMs > 0 ? endMs <= nowMs : true;
    });
    const auctionRuns = rows.length;
    const avgHighBid =
      auctionRuns > 0
        ? rows.reduce((sum, row) => sum + safeNum(row?.bid ?? row?.highBid ?? 0), 0) / auctionRuns
        : 0;
    const avgRetail =
      auctionRuns > 0
        ? rows.reduce((sum, row) => sum + safeNum(row?.retailPrice ?? row?.listernautRetailPrice ?? 0), 0) / auctionRuns
        : 0;
    const targetCost = avgRetail * (safeNum(upcHistoryRetailSpendPct) / 100);
    const difference = avgHighBid - targetCost;
    const recommendation =
      auctionRuns <= 0
        ? "No auction history for this filter yet."
        : difference >= 0.01
          ? "Good deal to buy."
          : difference <= -0.01
            ? "Do not buy."
            : "Borderline buy.";
    const recommendationTone =
      auctionRuns <= 0
        ? "#64748b"
        : difference >= 0.01
          ? "#166534"
          : difference <= -0.01
            ? "#991b1b"
            : "#92400e";
    const recommendationBg =
      auctionRuns <= 0
        ? "#f1f5f9"
        : difference >= 0.01
          ? "#dcfce7"
          : difference <= -0.01
            ? "#fee2e2"
            : "#fef3c7";
    return {
      auctionRuns,
      avgHighBid,
      avgRetail,
      targetCost,
      difference,
      recommendation,
      recommendationTone,
      recommendationBg,
    };
  }, [safeNum, upcHistoryFilteredRows, upcHistoryRetailSpendPct, upcHistoryRunningAuctionsFilter]);

  const upcHistoryDisplayedRows = useMemo(() => {
    const sourceRows = upcHistoryTab === "passed" ? upcHistoryDialog.passedRows : upcHistoryDialog.soldRows;
    const consignorFilteredRows =
      upcHistoryConsignorFilter === "house"
        ? (Array.isArray(sourceRows) ? sourceRows : []).filter((row) => isHouseSellerRow(row))
        : Array.isArray(sourceRows)
          ? sourceRows
          : [];
    return getSortedUpcHistoryRows(consignorFilteredRows);
  }, [
    getSortedUpcHistoryRows,
    isHouseSellerRow,
    upcHistoryConsignorFilter,
    upcHistoryDialog.passedRows,
    upcHistoryDialog.soldRows,
    upcHistoryTab,
  ]);

  const renderExpandableTextPreview = useCallback(
    (value, title = "Full Text", maxLength = PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH) => {
      const text = String(value || "").trim();
      if (!text) return "—";
      const truncated = truncateProductSearchPreviewText(text, maxLength);
      const shouldShowFull = truncated !== text;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ color: "#374151", wordBreak: "break-word" }}>
            {truncated}
          </Typography>
          {shouldShowFull ? (
            <Button
              size="small"
              onClick={() => openFullTextDialog(title, text)}
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 0,
                py: 0,
                fontWeight: 900,
                color: primary,
                "&:hover": { background: "transparent", textDecoration: "underline" },
              }}
            >
              View Full
            </Button>
          ) : null}
        </Box>
      );
    },
    [openFullTextDialog, primary]
  );

  const renderDescriptionPreview = useCallback(
    (value, title = "Full Description") =>
      renderExpandableTextPreview(value, title, PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH),
    [renderExpandableTextPreview]
  );

  const toggleMediaUrlsExpanded = useCallback((key) => {
    const safeKey = String(key || "").trim();
    if (!safeKey) return;
    setExpandedMediaUrlsByKey((prev) => ({ ...prev, [safeKey]: !prev?.[safeKey] }));
  }, []);

  const getRawProductSearchCellValue = useCallback(
    (row, colKey) =>
      getProductSearchRawCellValue(row, colKey, {
        getProductSearchThumbnailUrl,
        getLotDescriptionText,
        formatLotDimensions,
        getListernautMediaUrls,
      }),
    [formatLotDimensions, getListernautMediaUrls, getLotDescriptionText, getProductSearchThumbnailUrl]
  );

  const renderProductSearchCell = useCallback(
    (row, colKey) => {
      const key = String(colKey || "").trim();
      if (key === "thumbnail") {
        const thumbnailUrl = String(getRawProductSearchCellValue(row, key) || "").trim();
        return thumbnailUrl ? (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={String(row?.listernautTitle || row?.title || row?.lot || "Lot image")}
            sx={{
              width: 52,
              height: 52,
              objectFit: "cover",
              borderRadius: 1.5,
              border: "1px solid rgba(148,163,184,0.28)",
              background: "#fff",
            }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 800 }}>
            —
          </Typography>
        );
      }
      if (key === "title") {
        const titleText = String(getRawProductSearchCellValue(row, key) || "").trim();
        return renderExpandableTextPreview(
          titleText,
          titleText || `Lot ${String(row?.lot || "").trim() || "Title"}`,
          PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH
        );
      }
      if (key === "description") {
        return renderDescriptionPreview(
          getLotDescriptionText(row),
          `Lot ${String(row?.lot || "").trim() || "Description"}`
        );
      }
      if (key === "mediaUrls") {
        const urls = String(getRawProductSearchCellValue(row, key) || "")
          .split(/\r?\n+/)
          .map((item) => String(item || "").trim())
          .filter(Boolean);
        if (!urls.length) return "—";
        const expandKey = `product-${String(row?.id || row?.lot || row?.auctionId || "row")}-mediaUrls`;
        const isExpanded = !!expandedMediaUrlsByKey?.[expandKey];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, minWidth: 140 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => toggleMediaUrlsExpanded(expandKey)}
              sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800, minWidth: 0, px: 1.2, py: 0.2 }}
            >
              {isExpanded ? "Hide URLs" : `Show URLs (${formatNumber2(urls.length)})`}
            </Button>
            {isExpanded ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, width: "100%" }}>
                {urls.map((url, idx) => (
                  <Typography
                    key={`${row?.id || row?.lot || "lot"}-media-${idx}`}
                    variant="caption"
                    sx={{ color: primary, wordBreak: "break-all" }}
                  >
                    {url}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        );
      }
      if (key === "upcAuctionRuns") {
        const soldCount = toAbsInt(row?.upcOccurrenceSoldCount || 0);
        const totalCount = toAbsInt(row?.upcOccurrenceTotalCount || 0);
        if (!totalCount) return "—";
        return (
          <Button
            size="small"
            onClick={() => openUpcHistoryDialog(row)}
            sx={{
              textTransform: "none",
              minWidth: 0,
              px: 0,
              py: 0,
              fontWeight: 900,
              color: primary,
              "&:hover": { background: "transparent", textDecoration: "underline" },
            }}
          >
            {`${formatNumber2(soldCount)}/${formatNumber2(totalCount)}`}
          </Button>
        );
      }
      return getProductSearchCellValue(row, colKey, {
        getProductSearchThumbnailUrl,
        getLotDescriptionText,
        formatLotDimensions,
        getListernautMediaUrls,
        safeNum,
        moneyPlain,
        formatNumber2,
        toAbsInt,
      });
    },
    [
      expandedMediaUrlsByKey,
      formatLotDimensions,
      formatNumber2,
      getListernautMediaUrls,
      getLotDescriptionText,
      getProductSearchThumbnailUrl,
      getRawProductSearchCellValue,
      moneyPlain,
      openUpcHistoryDialog,
      primary,
      renderDescriptionPreview,
      renderExpandableTextPreview,
      safeNum,
      toAbsInt,
      toggleMediaUrlsExpanded,
    ]
  );

  return (
    <Paper sx={{ borderRadius: 3, p: 2, border: "1px solid rgba(51,57,153,0.10)", width: "100%", minWidth: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>Product Search</Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
            Search across auction, auction end date, lot, title, brand, condition, manufacturer, consigner, bidder, email, user type, UPC, lister, location, color, weight, dimensions, media, pricing, and occurrence history.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Chip
            size="small"
            label={
              productSearchBusy
                ? `Loaded ${formatNumber2(productSearchLoadedCount)}${productSearchTotalCount > 0 ? ` / ${formatNumber2(productSearchTotalCount)}` : ""}`
                : `${formatNumber2(productSearchAllRowsCount)} lots loaded`
            }
            sx={{ borderRadius: 999, fontWeight: 900 }}
          />
          <Chip
            size="small"
            label="24h cache"
            sx={{ borderRadius: 999, fontWeight: 900, background: "#eef2ff", color: "#333999" }}
          />
          <Button
            size="small"
            onClick={onOpenRefreshConfirm}
            disabled={productSearchBusy}
            startIcon={productSearchBusy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#b45309,#d97706)",
              "&:hover": { background: "linear-gradient(90deg,#92400e,#b45309)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
            }}
          >
            Refresh Anyway
          </Button>
          <Button
            size="small"
            onClick={() => void onExportFilteredCsv()}
            disabled={productSearchExportBusy || productSearchBusy}
            startIcon={productSearchExportBusy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#0284c7,#0369a1)",
              "&:hover": { background: "linear-gradient(90deg,#0369a1,#075985)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
            }}
          >
            {productSearchExportBusy ? "Preparing CSV..." : "Download CSV (Filtered Rows)"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", gap: 1.5, alignItems: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={productSearchField}
            onChange={(e) => setProductSearchField(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="all">All Fields</MenuItem>
            <MenuItem value="auctionId">Auction</MenuItem>
            <MenuItem value="auctionEndDate">Auction End Date</MenuItem>
            <MenuItem value="bidderName">Bidder Name</MenuItem>
            <MenuItem value="bids">Bids</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
            <MenuItem value="color">Color</MenuItem>
            <MenuItem value="condition">Condition</MenuItem>
            <MenuItem value="consignor">Consigner</MenuItem>
            <MenuItem value="description">Details</MenuItem>
            <MenuItem value="dimensions">Dimensions</MenuItem>
            <MenuItem value="emailLower">Email</MenuItem>
            <MenuItem value="lister">Lister</MenuItem>
            <MenuItem value="location">Location</MenuItem>
            <MenuItem value="lot">Lot Number</MenuItem>
            <MenuItem value="manufacturer">Manufacturer</MenuItem>
            <MenuItem value="mediaUrls">Media</MenuItem>
            <MenuItem value="quantity">Quantity</MenuItem>
            <MenuItem value="retailPrice">Retail</MenuItem>
            <MenuItem value="sellingToRetail">Selling $ to Retail</MenuItem>
            <MenuItem value="startBid">Start Bid</MenuItem>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="upc">UPC</MenuItem>
            <MenuItem value="upcAuctionRuns">UPC Occurrence</MenuItem>
            <MenuItem value="userType">User Type</MenuItem>
            <MenuItem value="views">Views</MenuItem>
            <MenuItem value="watches">Watches</MenuItem>
            <MenuItem value="weight">Weight</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          label="Search Products"
          placeholder={productSearchField === "all" ? "Search all fields..." : `Search by ${getProductSearchColumnLabel(productSearchField)}...`}
          value={productSearchSearchInput}
          onChange={(e) => {
            setProductSearchSearchInput(e.target.value);
            setProductSearchVisibleCount(200);
          }}
          InputProps={{
            endAdornment: productSearchSearchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setProductSearchSearchInput("");
                    setProductSearchVisibleCount(200);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {productSearchErr ? (
        <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
          {productSearchErr}
        </Alert>
      ) : null}

      {productSearchLoadedOnce && !productSearchBusy ? (
        <Typography variant="body2" sx={{ mt: 1, color: "#6b7280", fontWeight: 600 }}>
          Showing {productSearchRows.length.toLocaleString()} of {productSearchSortedLength.toLocaleString()} results
          {productSearchSearchInput ? ` (${productSearchAllRowsCount.toLocaleString()} total)` : ""}
        </Typography>
      ) : null}

      <Dialog
        open={productSearchBusy}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 320,
            maxWidth: 380,
            p: 0.5,
          },
        }}
      >
        <DialogContent sx={{ py: 3.5, px: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={30} />
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>
              Loading products, please wait
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
              Loaded {formatNumber2(productSearchLoadedCount)}
              {productSearchTotalCount > 0 ? ` / ${formatNumber2(productSearchTotalCount)}` : ""}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={fullTextDialog.open}
        onClose={closeFullTextDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
          {fullTextDialog.title || "Full Description"}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.65,
            }}
          >
            {fullTextDialog.text || "—"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
          <Button
            onClick={closeFullTextDialog}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#6b7280,#4b5563)",
              "&:hover": { background: "linear-gradient(90deg,#4b5563,#374151)" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={upcHistoryDialog.open}
        onClose={closeUpcHistoryDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
          {`UPC Occurrence History${upcHistoryDialog.upc ? `: ${upcHistoryDialog.upc}` : ""}`}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
          <Box
            sx={{
              mb: 1.75,
              p: 1.6,
              borderRadius: 2.4,
              border: "1px solid rgba(148,163,184,0.26)",
              background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>Should I Buy It?</Typography>
            <Typography variant="body2" sx={{ mt: 0.35, color: "#64748b" }}>
              Compare filtered average high bid against how much of retail you are willing to spend.
            </Typography>

            <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.2 }}>
              <FormControl size="small" fullWidth>
                <Select
                  value={upcHistoryConsignorFilter}
                  onChange={(e) => setUpcHistoryConsignorFilter(String(e.target.value || "all"))}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="all">All Consignors</MenuItem>
                  <MenuItem value="house">House Seller</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <Select
                  value={upcHistoryOutcomeFilter}
                  onChange={(e) => setUpcHistoryOutcomeFilter(String(e.target.value || "sold"))}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="sold">Only Sold</MenuItem>
                  <MenuItem value="all">All Sold + Passed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <Select
                  value={upcHistoryRunningAuctionsFilter}
                  onChange={(e) => setUpcHistoryRunningAuctionsFilter(String(e.target.value || "exclude"))}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="exclude">Exclude Running Auctions</MenuItem>
                  <MenuItem value="include">Include Running Auctions</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
              <Box sx={{ p: 1.15, borderRadius: 1.8, background: "#eef2ff", border: "1px solid #c7d2fe" }}>
                <Typography variant="caption" sx={{ display: "block", color: "#4338ca", fontWeight: 900 }}>
                  Auction Runs
                </Typography>
                <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                  {formatNumber2(upcHistoryBuyCalculator.auctionRuns)}
                </Typography>
              </Box>
              <Box sx={{ p: 1.15, borderRadius: 1.8, background: "#ecfeff", border: "1px solid #a5f3fc" }}>
                <Typography variant="caption" sx={{ display: "block", color: "#0f766e", fontWeight: 900 }}>
                  Avg High Bid
                </Typography>
                <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                  ${moneyPlain(upcHistoryBuyCalculator.avgHighBid || 0)}
                </Typography>
              </Box>
              <Box sx={{ p: 1.15, borderRadius: 1.8, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <Typography variant="caption" sx={{ display: "block", color: "#166534", fontWeight: 900 }}>
                  Avg Retail
                </Typography>
                <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                  ${moneyPlain(upcHistoryBuyCalculator.avgRetail || 0)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, px: 0.35 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                  Willing To Spend: {formatNumber2(toAbsInt(upcHistoryRetailSpendPct))}% of retail
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 800 }}>
                  Your cost: ${moneyPlain(upcHistoryBuyCalculator.targetCost || 0)}
                </Typography>
              </Box>
              <Slider
                value={upcHistoryRetailSpendPct}
                min={0}
                max={100}
                step={5}
                onChange={(_, value) => setUpcHistoryRetailSpendPct(Array.isArray(value) ? Number(value[0] || 0) : Number(value || 0))}
                sx={{ mt: 0.25, color: primary }}
              />
            </Box>

            <Box
              sx={{
                mt: 1.15,
                p: 1.25,
                borderRadius: 1.8,
                background: upcHistoryBuyCalculator.recommendationBg,
                border: `1px solid ${upcHistoryBuyCalculator.recommendationTone}22`,
              }}
            >
              <Typography sx={{ color: upcHistoryBuyCalculator.recommendationTone, fontWeight: 900 }}>
                Potential High Bid vs Your Cost
              </Typography>
              <Typography sx={{ mt: 0.3, color: "#111827", fontWeight: 800 }}>
                ${moneyPlain(upcHistoryBuyCalculator.avgHighBid || 0)} - ${moneyPlain(upcHistoryBuyCalculator.targetCost || 0)} = ${moneyPlain(upcHistoryBuyCalculator.difference || 0)}
              </Typography>
              <Typography sx={{ mt: 0.45, color: upcHistoryBuyCalculator.recommendationTone, fontWeight: 900 }}>
                {upcHistoryBuyCalculator.recommendation}
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1 }}>
            Matching Auction Runs
          </Typography>
          <Tabs
            value={upcHistoryTab}
            onChange={(_, value) => setUpcHistoryTab(String(value || "sold"))}
            sx={{ minHeight: 40, mb: 1.25 }}
          >
            <Tab
              value="sold"
              label={`Sold (${formatNumber2((upcHistoryDialog.soldRows || []).length)})`}
              sx={{ textTransform: "none", minHeight: 40, fontWeight: 900 }}
            />
            <Tab
              value="passed"
              label={`Passed (${formatNumber2((upcHistoryDialog.passedRows || []).length)})`}
              sx={{ textTransform: "none", minHeight: 40, fontWeight: 900 }}
            />
          </Tabs>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc", fontWeight: 900, color: primary } }}>
                  <TableCell>Auction</TableCell>
                  <TableCell>Lot #</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Consignor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Retail</TableCell>
                  <TableCell>High Bid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcHistoryDisplayedRows.length ? (
                  upcHistoryDisplayedRows.map((row, idx) => (
                    <TableRow key={`${upcHistoryTab}-${row?.id || row?.auctionId || "row"}-${row?.lot || idx}`}>
                      <TableCell>
                        {getProductSearchCellValue(row, "auctionId", {
                          getProductSearchThumbnailUrl,
                          getLotDescriptionText,
                          formatLotDimensions,
                          getListernautMediaUrls,
                          safeNum,
                          moneyPlain,
                          formatNumber2,
                          toAbsInt,
                        })}
                      </TableCell>
                      <TableCell>{String(row?.lot || "—").trim() || "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>{String(row?.title || row?.listernautTitle || "—").trim() || "—"}</TableCell>
                      <TableCell>{String(row?.consignor || row?.listernautConsignorName || row?.sellerCode || "—").trim() || "—"}</TableCell>
                      <TableCell>{safeNum(row?.bid || row?.highBid || 0) > 0 ? "Sold" : "Passed"}</TableCell>
                      <TableCell>{safeNum(row?.retailPrice || row?.listernautRetailPrice || 0) > 0 ? `$${moneyPlain(row?.retailPrice || row?.listernautRetailPrice || 0)}` : "—"}</TableCell>
                      <TableCell>{safeNum(row?.bid || 0) > 0 ? `$${moneyPlain(row?.bid || 0)}` : "—"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "#6b7280" }}>
                      {upcHistoryTab === "sold"
                        ? "No sold rows found for the current consignor filter."
                        : "No passed rows found for the current consignor filter."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
          <Button
            onClick={closeUpcHistoryDialog}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#6b7280,#4b5563)",
              "&:hover": { background: "linear-gradient(90deg,#4b5563,#374151)" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        ref={productSearchContainerRef}
        onScroll={(e) => {
          void onProductSearchScroll(e);
        }}
        component={Paper}
        sx={{
          mt: 1.5,
          borderRadius: 2,
          border: "1px solid rgba(51,57,153,0.10)",
          width: "100%",
          maxWidth: "100%",
          maxHeight: 620,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: "100%",
            width: "max-content",
            tableLayout: "auto",
          }}
        >
          <TableHead>
            <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
              {productSearchDisplayColumns.map((colKey) => (
                <TableCell key={colKey} sx={{ fontWeight: 900, color: primary, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={productSearchSortKey === colKey}
                    direction={productSearchSortKey === colKey ? productSearchSortDir : "asc"}
                    onClick={() => onProductSearchSort(colKey)}
                  >
                    {getProductSearchColumnLabel(colKey)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!productSearchBusy && productSearchRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(1, productSearchDisplayColumns.length)}>
                  <Box sx={{ p: 4, textAlign: "center", color: "#6b7280" }}>No lots found.</Box>
                </TableCell>
              </TableRow>
            ) : (
              productSearchRows.map((row) => (
                <TableRow key={row?.id || `${row?.auctionId || ""}__${row?.lot || ""}`} hover>
                  {productSearchDisplayColumns.map((colKey) => (
                    <TableCell key={`${row?.id || row?.lot || "lot"}__${colKey}`} sx={{ color: "#374151", verticalAlign: "top" }}>
                      {renderProductSearchCell(row, colKey)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 44, mt: 1 }}>
        {productSearchVisibleCount < productSearchSortedLength ? (
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Scroll to load more lots
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            End of lots
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export function ProductSearchRefreshDialog({
  open,
  onClose,
  onConfirm,
  productSearchBusy,
  modalPaperSx,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        Refresh Product Search?
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        <Typography sx={{ color: "#374151", lineHeight: 1.7 }}>
          Vrushab said this will cost money. Do you still want to do it?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            color: "#4b5563",
            background: "#e5e7eb",
            "&:hover": { background: "#d1d5db" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => void onConfirm()}
          disabled={productSearchBusy}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            color: "#fff",
            background: "linear-gradient(90deg,#b45309,#d97706)",
            "&:hover": { background: "linear-gradient(90deg,#92400e,#b45309)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
          }}
        >
          {productSearchBusy ? "Refreshing..." : "Continue Anyways"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
